/**
 * Google Identity Services integration. Loaded via the <script> in index.html;
 * we expose a typed `initGoogleSignIn` that takes a callback and renders the
 * GIS button into a container element.
 *
 * Uses the ID-token flow only (per ADR 0002). We never request OAuth scopes
 * — only identity, verified server-side by apps/api.
 */

declare global {
  interface Window {
    google?: GoogleNamespace;
  }
}

/** The subset of GIS's One Tap notification we act on. */
type GisPromptNotification = {
  isDisplayed?: () => boolean;
  isNotDisplayed?: () => boolean;
  getNotDisplayedReason?: () => string;
};

type GoogleNamespace = {
  accounts: {
    id: {
      initialize: (opts: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        opts: {
          type?: "standard" | "icon";
          theme?: "outline" | "filled_blue" | "filled_black";
          size?: "large" | "medium" | "small";
          text?: "signin_with" | "signup_with" | "continue_with" | "signin";
          shape?: "rectangular" | "pill" | "circle" | "square";
          width?: number;
          // GIS's own click hook. A plain listener on the container can NEVER
          // work: Google renders the button inside a cross-origin iframe, and a
          // click in there does not bubble to our document. This is the only way
          // to tell "never tried" from "tried and Google's flow went nowhere".
          click_listener?: () => void;
        },
      ) => void;
      prompt: (cb?: (notification: GisPromptNotification) => void) => void;
      disableAutoSelect: () => void;
    };
  };
};

const CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string | undefined;

export function getClientId(): string {
  if (!CLIENT_ID) {
    throw new Error(
      "VITE_GOOGLE_OAUTH_CLIENT_ID is not set. Vite inlines it at BUILD time from\n"
      + "apps/learner/.env.production, NOT from wrangler.toml, whose [vars] block is a\n"
      + "Pages runtime var this static bundle never reads.",
    );
  }
  return CLIENT_ID;
}

/**
 * Wait until window.google is loaded (the <script> in index.html is async).
 * Resolves once the GIS namespace is available, or rejects after 5s.
 */
export function waitForGis(): Promise<GoogleNamespace> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const start = Date.now();
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(timer);
        resolve(window.google);
      } else if (Date.now() - start > 5000) {
        clearInterval(timer);
        reject(new Error("Google Identity Services failed to load within 5s."));
      }
    }, 50);
  });
}

export type GoogleSignInOptions = {
  onCredential: (idToken: string) => void;
  /** Container element to render the button into. */
  buttonContainer: HTMLElement;
  /** Show the One Tap prompt as well as the button. Default true. */
  oneTap?: boolean;
  /**
   * GIS has actually PAINTED the button. `initialize` and `renderButton` both
   * return without throwing when nothing appears, so "init succeeded" is not
   * evidence the student was offered anything to click.
   */
  onButtonRendered?: () => void;
  /** The student clicked Google's button. */
  onButtonClick?: () => void;
  /** One Tap was shown. It is a SECOND route to a credential, with no click. */
  onOneTapDisplayed?: () => void;
};

export async function initGoogleSignIn({
  onCredential,
  buttonContainer,
  oneTap = true,
  onButtonRendered,
  onButtonClick,
  onOneTapDisplayed,
}: GoogleSignInOptions): Promise<void> {
  const google = await waitForGis();
  google.accounts.id.initialize({
    client_id: getClientId(),
    callback: (response) => onCredential(response.credential),
    auto_select: false,
    cancel_on_tap_outside: true,
  });
  google.accounts.id.renderButton(buttonContainer, {
    type: "standard",
    theme: "filled_black",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    click_listener: () => onButtonClick?.(),
  });
  // renderButton injects an iframe asynchronously and reports nothing, so the
  // only way to know a button exists is to look for it. Polls for a short while
  // and then gives up silently: a missing call is the signal, and telemetry must
  // never keep a timer alive on a screen the student has left.
  if (onButtonRendered) {
    const deadline = Date.now() + 4000;
    const check = () => {
      if (!buttonContainer.isConnected) return;
      if (buttonContainer.childElementCount > 0) return onButtonRendered();
      if (Date.now() < deadline) setTimeout(check, 120);
    };
    check();
  }
  if (oneTap) {
    google.accounts.id.prompt((n) => {
      if (n?.isDisplayed?.()) onOneTapDisplayed?.();
    });
  }
}

/** Tear down GIS auto-select on logout. */
export function signOutGis(): void {
  window.google?.accounts.id.disableAutoSelect();
}
